/**
 * Sprint 58 — Fristen-Dashboard / Deadline Engine
 *
 * Components:
 * - DeadlineDashboard: main container (fetches /subsidies/deadlines)
 * - HeroCountdown: large countdown for most urgent deadline
 * - DeadlineTimeline: vertical list with urgency dots + expandable cards
 * - CategoryFilter: filter tabs (Alle | GAP | AUKM | Compliance)
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';
import SubsidyChecklist from './SubsidyChecklist';

// ── Urgency config ──────────────────────────────────────────────────────────

const URGENCY_CONFIG = {
  PAST:     { color: 'text-[var(--aif-text-muted)]',    dot: 'bg-gray-500',   border: 'border-gray-600',   bg: 'bg-gray-800/30' },
  CRITICAL: { color: 'text-red-400',                    dot: 'bg-red-500',    border: 'border-red-500',    bg: 'bg-red-900/20'  },
  URGENT:   { color: 'text-orange-400',                 dot: 'bg-orange-400', border: 'border-orange-500', bg: 'bg-orange-900/20' },
  UPCOMING: { color: 'text-yellow-400',                 dot: 'bg-yellow-400', border: 'border-yellow-600', bg: 'bg-yellow-900/10' },
  FUTURE:   { color: 'text-green-400',                  dot: 'bg-green-500',  border: 'border-green-700',  bg: 'bg-green-900/10' },
};

function urgencyCfg(urgency) {
  return URGENCY_CONFIG[urgency] || URGENCY_CONFIG.FUTURE;
}

// ── Helper: format deadline date (de-DE) ───────────────────────────────────

function fmtDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── HeroCountdown ──────────────────────────────────────────────────────────

const CHECKLIST_IDS = ['de_agrarantrag_2026', 'at_mfa_2026', 'ch_direktzahlungen_2026'];

function HeroCountdown({ deadline, onStartChecklist }) {
  const { t } = useTranslation();
  if (!deadline) return null;

  const hasChecklist = CHECKLIST_IDS.includes(deadline.id);

  const cfg = urgencyCfg(deadline.urgency);
  const isPulsing = deadline.urgency === 'CRITICAL';
  const days = deadline.days_remaining;
  // Progress bar: 100% = 90 days away, 0% = today
  const progressPct = Math.max(0, Math.min(100, (days / 90) * 100));

  return (
    <div
      data-testid="hero-countdown"
      className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-5 mb-4`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className={`text-xs font-semibold uppercase tracking-wide ${cfg.color} mb-1`}>
            {t(`deadline.urgency.${deadline.urgency}`, deadline.urgency)}
          </div>
          <h3 className="text-base font-bold text-[var(--aif-text-primary)] leading-tight">
            {deadline.name}
          </h3>
          {deadline.category && (
            <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]">
              {deadline.category}
            </span>
          )}
        </div>

        {/* Countdown badge */}
        <div className={`text-right shrink-0 ${isPulsing ? 'animate-pulse' : ''}`}>
          {days >= 0 ? (
            <>
              <div className={`text-4xl font-black ${cfg.color}`}>{days}</div>
              <div className="text-xs text-[var(--aif-text-muted)]">{t('deadline.daysLeft')}</div>
            </>
          ) : (
            <div className="text-2xl font-black text-[var(--aif-text-muted)]">{t('deadline.past')}</div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {days >= 0 && (
        <div className="mb-3">
          <div className="h-2 rounded-full bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] overflow-hidden">
            <div
              data-testid="countdown-progress"
              className={`h-full rounded-full transition-all ${
                deadline.urgency === 'CRITICAL' ? 'bg-red-500' :
                deadline.urgency === 'URGENT'   ? 'bg-orange-400' :
                deadline.urgency === 'UPCOMING' ? 'bg-yellow-400' : 'bg-green-500'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-[var(--aif-text-muted)] mt-1">
            <span>{t('deadline.today')}</span>
            <span>{fmtDate(deadline.deadline)}</span>
          </div>
        </div>
      )}

      {/* Description */}
      {deadline.description && (
        <p className="text-xs text-[var(--aif-text-muted)] mb-3 leading-relaxed">
          {deadline.description}
        </p>
      )}

      {/* Penalty warning */}
      {deadline.penalty && (
        <div className="flex items-start gap-2 text-xs text-orange-300 bg-orange-900/20 border border-orange-800/40 rounded-xl px-3 py-2 mb-3">
          <span className="mt-0.5 shrink-0">⚠️</span>
          <span>{deadline.penalty}</span>
        </div>
      )}

      {/* Action buttons row */}
      <div className="flex gap-2">
        {/* Vorbereitung starten button */}
        {hasChecklist && onStartChecklist && (
          <button
            onClick={() => onStartChecklist(deadline.id)}
            data-testid="start-checklist-btn"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-[var(--aif-color-primary)] hover:opacity-90 text-white transition-all"
          >
            ✓ {t('checklist.startPrep')}
          </button>
        )}

        {/* Portal link */}
        {deadline.portal_url && (
          <a
            href={deadline.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portal-link"
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              hasChecklist ? 'px-4' : 'flex-1'
            } ${
              deadline.urgency === 'CRITICAL' ? 'bg-red-600 hover:bg-red-700 text-white' :
              deadline.urgency === 'URGENT'   ? 'bg-orange-600 hover:bg-orange-700 text-white' :
              'bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] hover:border-[var(--aif-color-primary)]'
            }`}
          >
            {t('deadline.goToPortal')} →
          </a>
        )}
      </div>
    </div>
  );
}

// ── DeadlineCard (expanded in timeline) ───────────────────────────────────

function DeadlineCard({ deadline, expanded, onToggle, onStartChecklist, checklistPct }) {
  const { t } = useTranslation();
  const cfg = urgencyCfg(deadline.urgency);
  const isPast = deadline.urgency === 'PAST';
  const hasChecklist = CHECKLIST_IDS.includes(deadline.id);

  return (
    <div
      className={`rounded-xl border ${cfg.border} ${cfg.bg} overflow-hidden transition-all`}
    >
      {/* Collapsed row */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={onToggle}
        data-testid={`deadline-row-${deadline.id}`}
      >
        {/* Urgency dot */}
        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot} ${
          deadline.urgency === 'CRITICAL' ? 'animate-pulse' : ''
        }`} />

        {/* Name + date */}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium truncate ${isPast ? 'line-through text-[var(--aif-text-muted)]' : 'text-[var(--aif-text-primary)]'}`}>
            {deadline.name}
          </div>
          <div className={`text-[11px] ${cfg.color}`}>
            {fmtDate(deadline.deadline)}
            {!isPast && deadline.days_remaining >= 0 && (
              <span className="ml-2 text-[var(--aif-text-muted)]">
                ({deadline.days_remaining} {t('deadline.daysLeft')})
              </span>
            )}
          </div>
        </div>

        {/* Category + completion badge */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]">
            {deadline.category}
          </span>
          {hasChecklist && checklistPct !== undefined && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              checklistPct === 100 ? 'bg-green-500/20 text-green-400' :
              checklistPct > 0    ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)]'
            }`} data-testid={`checklist-pct-${deadline.id}`}>
              {checklistPct}%
            </span>
          )}
        </div>

        {/* Expand chevron */}
        <span className="text-[var(--aif-text-muted)] text-xs shrink-0">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[var(--aif-border-default)] space-y-3">
          {deadline.description && (
            <p className="text-xs text-[var(--aif-text-muted)] leading-relaxed">
              {deadline.description}
            </p>
          )}

          {deadline.affects?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-[var(--aif-text-muted)] font-semibold mb-1">
                {t('deadline.affects')}
              </div>
              <div className="flex flex-wrap gap-1">
                {deadline.affects.map((a, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] border border-[var(--aif-border-default)]">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {deadline.conditions && (
            <div className="text-xs text-[var(--aif-text-muted)] italic">
              ℹ️ {deadline.conditions}
            </div>
          )}

          {deadline.penalty && (
            <div className="text-xs text-orange-300">
              ⚠️ {deadline.penalty}
            </div>
          )}

          {deadline.portal_url && (
            <a
              href={deadline.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[var(--aif-color-primary)] hover:underline"
            >
              🔗 {deadline.portal || t('deadline.goToPortal')} →
            </a>
          )}

          {/* Checklist CTA in expanded card */}
          {hasChecklist && onStartChecklist && (
            <button
              onClick={() => onStartChecklist(deadline.id)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/40 hover:bg-[var(--aif-color-primary)]/30 transition-all"
              data-testid={`card-start-checklist-${deadline.id}`}
            >
              ✓ {t('checklist.startPrep')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── CategoryFilter ──────────────────────────────────────────────────────────

const CATEGORIES = ['ALL', 'GAP', 'AUKM', 'COMPLIANCE', 'DIREKTZAHLUNG'];

function CategoryFilter({ active, onChange }) {
  const { t } = useTranslation();

  return (
    <div
      className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 mb-4"
      data-testid="category-filter"
    >
      {CATEGORIES.map(cat => (
        <button
          key={cat}
          onClick={() => onChange(cat)}
          data-testid={`filter-${cat.toLowerCase()}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
            active === cat
              ? 'bg-[var(--aif-color-primary)] text-white'
              : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
          }`}
        >
          {t(`deadline.category.${cat}`, cat)}
        </button>
      ))}
    </div>
  );
}

// ── DeadlineDashboard (main) ────────────────────────────────────────────────

export default function DeadlineDashboard({ token }) {
  const { t } = useTranslation();
  const [deadlines, setDeadlines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [checklistPcts, setChecklistPcts] = useState({});

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch('/api/v1/aif/subsidies/deadlines', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const dlList = d?.deadlines || [];
        setDeadlines(dlList);
        setLoading(false);
        // Pre-fetch checklist completion % for known checklist deadlines
        dlList.filter(dl => CHECKLIST_IDS.includes(dl.id)).forEach(dl => {
          fetch(`/api/v1/aif/subsidies/checklist/${dl.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then(r => r.ok ? r.json() : null)
            .then(c => {
              if (c) {
                setChecklistPcts(prev => ({ ...prev, [dl.id]: c.completion_pct }));
              }
            })
            .catch(() => {});
        });
      })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent" />
      </div>
    );
  }

  if (!deadlines?.length) {
    return (
      <div className="text-sm text-[var(--aif-text-muted)] text-center py-8">
        {t('deadline.noDeadlines')}
      </div>
    );
  }

  // Checklist view
  if (activeChecklist) {
    return (
      <SubsidyChecklist
        deadlineId={activeChecklist}
        token={token}
        onBack={() => setActiveChecklist(null)}
      />
    );
  }

  // Filter
  const filtered = category === 'ALL'
    ? deadlines
    : deadlines.filter(d => d.category?.toUpperCase() === category);

  // Hero = first non-past deadline from the full list
  const hero = deadlines.find(d => d.urgency !== 'PAST');

  return (
    <div className="space-y-2" data-testid="deadline-dashboard">
      {/* Hero countdown */}
      {hero && (
        <HeroCountdown
          deadline={hero}
          onStartChecklist={setActiveChecklist}
        />
      )}

      {/* Category filter */}
      <CategoryFilter active={category} onChange={setCategory} />

      {/* Deadline timeline */}
      <div className="space-y-2">
        {filtered.map(d => (
          <DeadlineCard
            key={d.id}
            deadline={d}
            expanded={expandedId === d.id}
            onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
            onStartChecklist={setActiveChecklist}
            checklistPct={checklistPcts[d.id]}
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-[var(--aif-text-muted)] text-center py-4">
            {t('deadline.noDeadlinesInCategory')}
          </div>
        )}
      </div>
    </div>
  );
}
